/**
 * Presents the intentionally small landing dashboard for internal users.
 */

import { useCallback, useEffect, useState } from "react";

import {
  InternalApiError,
  type InternalCaseApiClient,
} from "@libro-reclamos/api-client/InternalCases";
import type { InternalCaseListResponse } from "@libro-reclamos/contracts/InternalCases";

import { StatusBadge } from "../../../cases/components/atoms/StatusBadge";
import { CaseReasonAnalyticsPanel } from "./CaseReasonAnalyticsPanel";
import { CalculateDashboardSummary, type DashboardSummary } from "../../DashboardSummary";

const EmptySummary: DashboardSummary = Object.freeze({
  closedCases: 0,
  openCases: 0,
  pendingCases: 0,
});

type RecentCase = InternalCaseListResponse["items"][number];

interface DashboardHomeProps {
  readonly client: InternalCaseApiClient;
  readonly onOpenCase: (publicId: string) => void;
  readonly onOpenQueue: () => void;
  readonly onSessionExpired: () => void;
  readonly revision: number;
  /**
   * Whether the plan includes the reason analytics panel. The panel calls the
   * case-reason API, which a plan without the capability refuses, so hiding it
   * here avoids showing an error the customer cannot act on.
   */
  readonly showReasonAnalytics: boolean;
}

function LocalDate(value: string): string {
  return new Intl.DateTimeFormat("es-PE", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function DashboardErrorMessage(error: unknown): string {
  if (error instanceof InternalApiError) {
    if (error.status === 401) {
      return "La sesión expiró. Vuelve a iniciar sesión.";
    }
    if (error.status === 403) {
      return "Tu cuenta no tiene acceso al resumen de esta compañía.";
    }
  }
  return "No pudimos cargar el resumen. Comprueba tu conexión e inténtalo nuevamente.";
}

/**
 * Loads only three lightweight totals and the five latest authorized cases.
 */
export function DashboardHome({
  client,
  onOpenCase,
  onOpenQueue,
  onSessionExpired,
  revision,
  showReasonAnalytics,
}: DashboardHomeProps) {
  const [summary, setSummary] = useState<DashboardSummary>(EmptySummary);
  const [recentCases, setRecentCases] = useState<readonly RecentCase[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string | null>(null);
  const [reloadSequence, setReloadSequence] = useState(0);

  const Load = useCallback(
    async (signal: AbortSignal): Promise<void> => {
      setLoading(true);
      try {
        const [latest, pending, closed] = await Promise.all([
          client.list(
            {
              assignment: "ALL",
              page: 1,
              pageSize: 5,
              sort: "RECEIVED_DESC",
            },
            signal,
          ),
          client.list(
            {
              assignment: "ALL",
              page: 1,
              pageSize: 1,
              sort: "RECEIVED_DESC",
              status: "PENDING_ASSIGNMENT",
            },
            signal,
          ),
          client.list(
            {
              assignment: "ALL",
              page: 1,
              pageSize: 1,
              sort: "RECEIVED_DESC",
              status: "CLOSED",
            },
            signal,
          ),
        ]);
        setSummary(CalculateDashboardSummary(latest.total, pending.total, closed.total));
        setRecentCases(latest.items);
        setMessage(null);
      } catch (error: unknown) {
        if (signal.aborted) {
          return;
        }
        if (error instanceof InternalApiError && error.status === 401) {
          onSessionExpired();
        }
        setMessage(DashboardErrorMessage(error));
      } finally {
        if (!signal.aborted) {
          setLoading(false);
        }
      }
    },
    [client, onSessionExpired],
  );

  useEffect(() => {
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      void Load(controller.signal);
    }, 0);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [Load, reloadSequence, revision]);

  const Count = (value: number): string => (loading ? "—" : String(value));

  return (
    <div className="grid gap-5">
      <section aria-label="Resumen de casos" className="grid gap-3 sm:grid-cols-3">
        <article className="rounded-[var(--radius-medium)] border border-l-[3px] border-[var(--color-border-default)] border-l-[var(--color-brand-primary)] bg-white p-5">
          <p className="m-0 text-xs font-semibold text-[var(--color-text-muted)]">Casos abiertos</p>
          <p className="mt-2 mb-0 text-3xl font-bold text-[var(--color-brand-deep)]">
            {Count(summary.openCases)}
          </p>
        </article>
        <article className="rounded-[var(--radius-medium)] border border-l-[3px] border-[var(--color-border-default)] border-l-[var(--color-status-warning)] bg-white p-5">
          <p className="m-0 text-xs font-semibold text-[var(--color-text-muted)]">
            Pendientes de asignación
          </p>
          <p className="mt-2 mb-0 text-3xl font-bold text-[var(--color-status-warning)]">
            {Count(summary.pendingCases)}
          </p>
        </article>
        <article className="rounded-[var(--radius-medium)] border border-l-[3px] border-[var(--color-border-default)] border-l-[var(--color-status-success)] bg-white p-5">
          <p className="m-0 text-xs font-semibold text-[var(--color-text-muted)]">Casos cerrados</p>
          <p className="mt-2 mb-0 text-3xl font-bold text-[var(--color-status-success)]">
            {Count(summary.closedCases)}
          </p>
        </article>
      </section>

      {message === null ? null : (
        <div
          className="flex flex-wrap items-center justify-between gap-3 rounded-[var(--radius-small)] border border-[var(--color-border-default)] bg-white px-4 py-3 text-sm"
          role="alert"
        >
          <span>{message}</span>
          <button
            className="font-semibold text-[var(--color-brand-primary)]"
            onClick={() => {
              setReloadSequence((current) => current + 1);
            }}
            type="button"
          >
            Reintentar
          </button>
        </div>
      )}

      {showReasonAnalytics ? (
        <CaseReasonAnalyticsPanel client={client} revision={revision} />
      ) : null}

      <section className="overflow-hidden rounded-[var(--radius-medium)] border border-[var(--color-border-default)] bg-white">
        <div className="flex items-center justify-between gap-3 border-b border-[var(--color-border-default)] px-5 py-4">
          <div>
            <h2 className="m-0 text-sm font-bold text-[var(--color-text-default)]">
              Últimos casos registrados
            </h2>
            <p className="mt-1 mb-0 text-xs text-[var(--color-text-muted)]">
              Los cinco ingresos más recientes que puedes consultar.
            </p>
          </div>
          <button
            className="shrink-0 text-sm font-semibold text-[var(--color-brand-primary)] hover:text-[var(--color-brand-deep)]"
            onClick={onOpenQueue}
            type="button"
          >
            Ver todos
          </button>
        </div>

        {loading ? (
          <p className="m-0 p-8 text-center text-sm text-[var(--color-text-muted)]">
            Cargando casos recientes…
          </p>
        ) : recentCases.length === 0 ? (
          <p className="m-0 p-8 text-center text-sm text-[var(--color-text-muted)]">
            Todavía no hay casos registrados.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[44rem] border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border-default)] text-xs font-semibold tracking-wide text-[var(--color-text-muted)] uppercase">
                  <th className="px-5 py-3">Caso</th>
                  <th className="px-5 py-3">Consumidor</th>
                  <th className="px-5 py-3">Estado</th>
                  <th className="px-5 py-3">Fecha de ingreso</th>
                  <th className="px-5 py-3 text-right">Acción</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--color-border-default)]">
                {recentCases.map((complaintCase) => (
                  <tr key={complaintCase.publicId}>
                    <td className="px-5 py-3 font-semibold text-[var(--color-brand-deep)]">
                      N.º {complaintCase.publicId}
                    </td>
                    <td className="px-5 py-3">{complaintCase.consumerName}</td>
                    <td className="px-5 py-3">
                      <StatusBadge
                        title={complaintCase.status.title}
                        value={complaintCase.status.value}
                      />
                    </td>
                    <td className="px-5 py-3 text-[var(--color-text-muted)]">
                      {LocalDate(complaintCase.receivedAt)}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <button
                        className="font-semibold text-[var(--color-brand-primary)] hover:text-[var(--color-brand-deep)]"
                        onClick={() => {
                          onOpenCase(complaintCase.publicId);
                        }}
                        type="button"
                      >
                        Abrir
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
